import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import {
  ArrowRight, ExternalLink, Mail, Building2, Globe, Calculator, Plane,
  ChevronDown, Menu, X, Settings, Check, Users, Sparkles, Languages,
  BookOpen, Rocket, Shield, ScrollText, Quote, Briefcase, AlertTriangle,
  ShieldCheck, Compass, Handshake, Award, Gem, ChevronRight, Target,
  TrendingUp, Lightbulb, Anchor, MapPin, Phone
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Separator } from "./components/ui/separator";
import { CookieConsentProvider, useCookieConsent } from "./components/CookieConsent";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import ContactForm from "./components/ContactForm";
import WhatsAppFloat from "./components/WhatsAppFloat";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import OperationPolicy from "./pages/OperationPolicy";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import VisitorTracker from "./components/VisitorTracker";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_finland-bridge/artifacts/s3kwjyk7_2Ds%20logo%20%281%29.JPG";

// ─────────────────────────────────────────────────────────────────────────────
// Language Switcher
// ─────────────────────────────────────────────────────────────────────────────
const LanguageSwitcher = ({ variant = "default" }) => {
  const { language, toggleLanguage } = useLanguage();
  const base = variant === "footer"
    ? "flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm"
    : "flex items-center gap-2 text-sm font-sans px-3 py-1.5 rounded-full border border-ink/20 text-ink hover:bg-ink hover:text-parchment transition-colors";
  return (
    <button
      onClick={toggleLanguage}
      className={base}
      data-testid="language-switcher"
      aria-label={language === 'en' ? 'Switch to Finnish' : 'Vaihda englanniksi'}
    >
      <Languages className="w-4 h-4" />
      <span className="font-medium">{language === 'en' ? 'FI' : 'EN'}</span>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Navigation
// ─────────────────────────────────────────────────────────────────────────────
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#barriers", label: t('nav.barriers') },
    { href: "#journey", label: t('nav.journey') },
    { href: "#services", label: t('nav.services') },
    { href: "#recognition", label: t('nav.recognition') },
    { href: "#ventures", label: t('nav.ventures') },
    { href: "#contact", label: t('nav.contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "nav-glass py-3" : "bg-transparent py-5"}`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <a href="#" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="2DS Finland Oy"
            className="h-11 md:h-12 w-auto object-contain"
            style={{ mixBlendMode: 'multiply', filter: 'contrast(1.15) brightness(1.05)' }}
          />
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-lg md:text-xl tracking-tight text-ink lining-nums">2DS Finland Oy</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-5" data-testid="desktop-nav">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} className="text-[13px] xl:text-sm font-sans text-ink-light hover:text-ink transition-colors link-underline whitespace-nowrap">
              {l.label}
            </a>
          ))}
          <LanguageSwitcher />
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-sm font-sans bg-accent-olive text-parchment px-5 py-2.5 rounded-full hover:bg-accent-olive-light transition-colors"
            data-testid="nav-cta-book"
          >
            <Mail className="w-4 h-4" />
            Book a call
          </a>
        </nav>

        <button
          className="lg:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <nav className="lg:hidden nav-glass mt-2 mx-4 rounded-lg p-6" data-testid="mobile-nav">
          <div className="flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-base font-sans text-ink-light hover:text-ink transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center justify-between py-2">
              <span className="text-ink-muted text-sm">Language</span>
              <LanguageSwitcher />
            </div>
            <a
              href="#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 text-sm font-sans bg-accent-olive text-parchment px-5 py-3 rounded-full mt-2"
              data-testid="mobile-cta-book"
            >
              <Mail className="w-4 h-4" />
              Book a call
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────
const HeroSection = () => {
  const { t } = useLanguage();
  return (
    <section className="min-h-screen flex flex-col justify-center relative pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <img
          src="/espoo-keilaniemi-hero.jpg"
          alt="Espoo Keilaniemi seaside business district, Finland"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-parchment via-parchment/95 to-parchment/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-parchment/40 via-transparent to-parchment" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-accent-olive/10 text-accent-olive px-4 py-2 rounded-full text-xs sm:text-sm mb-6 opacity-0 animate-fade-up backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5" />
              {t('hero.badge')}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6 opacity-0 animate-fade-up animation-delay-200">
              {t('hero.headline1')}<br />
              <span className="text-accent-olive">{t('hero.headline2')}</span><br />
              {t('hero.headline3')}
            </h1>
            <p
              className="font-sans text-base sm:text-lg md:text-xl text-ink-light leading-relaxed mb-8 opacity-0 animate-fade-up animation-delay-400"
              dangerouslySetInnerHTML={{ __html: t('hero.description') }}
            />

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-10 opacity-0 animate-fade-up animation-delay-600">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-accent-olive text-parchment px-7 py-3.5 rounded-full font-sans hover:bg-accent-olive-light hover:-translate-y-0.5 transition-all shadow-md"
                data-testid="hero-cta-primary"
              >
                {t('hero.ctaPrimary')}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#journey"
                className="inline-flex items-center justify-center gap-2 border-2 border-ink text-ink px-7 py-3.5 rounded-full font-sans hover:bg-ink hover:text-parchment transition-colors backdrop-blur-sm"
                data-testid="hero-cta-secondary"
              >
                {t('hero.ctaSecondary')}
              </a>
            </div>
          </div>

          <div className="opacity-0 animate-fade-up animation-delay-600">
            <div className="bg-white/85 backdrop-blur-md border border-warm rounded-2xl p-7 sm:p-8 shadow-2xl">
              <p className="text-xs tracking-[0.22em] uppercase text-ink-muted mb-6">{t('hero.statsTitle')}</p>
              <div className="grid grid-cols-2 gap-5 sm:gap-6">
                <div className="border-l-2 border-accent-olive pl-3 sm:pl-4">
                  <p className="font-serif text-3xl sm:text-4xl text-ink mb-1">4</p>
                  <p className="text-xs sm:text-sm text-ink-muted leading-tight">{t('hero.statsVentures')}</p>
                </div>
                <div className="border-l-2 border-accent-terracotta pl-3 sm:pl-4">
                  <p className="font-serif text-3xl sm:text-4xl text-ink mb-1">2021</p>
                  <p className="text-xs sm:text-sm text-ink-muted leading-tight">{t('hero.statsFounded')}</p>
                </div>
                <div className="border-l-2 border-accent-amber pl-3 sm:pl-4">
                  <p className="font-serif text-3xl sm:text-4xl text-ink mb-1">15+</p>
                  <p className="text-xs sm:text-sm text-ink-muted leading-tight">{t('hero.statsYears')}</p>
                </div>
                <div className="border-l-2 border-accent-indigo pl-3 sm:pl-4">
                  <p className="font-serif text-2xl sm:text-3xl text-ink mb-1">FI</p>
                  <p className="text-xs sm:text-sm text-ink-muted leading-tight">{t('hero.statsLocation')}</p>
                </div>
              </div>
              <Separator className="my-6 bg-warm" />
              <p className="text-xs text-ink-muted">{t('hero.businessId')}: 3246359-5</p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator z-10">
        <ChevronDown className="w-6 h-6 text-ink-muted" />
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Why Finland — Stats strip + 4 advantages + India corridor traction
// ─────────────────────────────────────────────────────────────────────────────
const MarketDriversSection = () => {
  const { t } = useLanguage();
  const india = t('marketDrivers.india');
  return (
    <section id="why-finland" className="bg-accent-olive text-parchment" data-testid="why-finland-section">
      {/* India corridor traction strip */}
      <div className="border-t border-parchment/15 bg-accent-olive-light/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 md:py-12" data-testid="india-corridor-strip">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
            <div className="lg:col-span-3">
              <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-accent-terracotta-light mb-3 inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                {india.label}
              </p>
              <p className="text-sm sm:text-base text-parchment/90 leading-relaxed">{india.body}</p>
            </div>
            <div className="lg:col-span-2 grid grid-cols-2 gap-3">
              {india.proofs.map((proof, i) => (
                <div key={i} className="bg-parchment/10 border border-parchment/20 rounded-lg px-3 py-2.5 text-xs sm:text-sm text-parchment/90 flex items-center gap-2" data-testid={`india-proof-${i}`}>
                  <Check className="w-3.5 h-3.5 text-accent-terracotta-light flex-shrink-0" />
                  <span>{proof}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sound Familiar? (Barriers) — compressed, no closing callout
// ─────────────────────────────────────────────────────────────────────────────
const BarriersSection = () => {
  const { t } = useLanguage();
  const items = t('barriers.items');
  return (
    <section id="barriers" className="py-20 md:py-28 lg:py-32 bg-cream" data-testid="barriers-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-terracotta mb-4">{t('barriers.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('barriers.headlineLead')}<br />
            <span className="italic text-accent-terracotta">{t('barriers.headlineEm')}</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-ink-light leading-relaxed">{t('barriers.description')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {items.map((item, idx) => (
            <article
              key={idx}
              className="bg-white border border-warm rounded-2xl p-6 md:p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              data-testid={`barrier-card-${idx}`}
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-accent-terracotta/10 text-accent-terracotta flex-shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <span className="inline-block text-[10px] sm:text-xs tracking-widest uppercase text-accent-terracotta mb-1">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-serif text-xl sm:text-2xl text-ink leading-tight">{item.title}</h3>
                </div>
              </div>
              <p className="text-sm sm:text-base text-ink-light leading-relaxed mb-4">{item.description}</p>

              <div className="mt-auto pt-4 border-t border-warm flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-accent-olive flex-shrink-0" />
                <div>
                  <p className="text-[10px] sm:text-xs tracking-widest uppercase text-accent-olive mb-1">
                    {t('barriers.ourSolution')}
                  </p>
                  <p className="text-sm sm:text-base text-ink leading-relaxed">{item.solution}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Co-founder Story — compressed: headline + distinction only (no 3 redundant columns)
// ─────────────────────────────────────────────────────────────────────────────
const CoFounderSection = () => {
  const { t } = useLanguage();
  const d = t('coFounder.distinction');
  return (
    <section id="co-founder" className="py-20 md:py-28 lg:py-32 bg-parchment" data-testid="co-founder-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-10 md:mb-12">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-olive mb-4">{t('coFounder.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('coFounder.headlineLead')}<br />
            <span className="italic text-accent-olive">{t('coFounder.headlineEm')}</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-ink-light leading-relaxed">{t('coFounder.description')}</p>
        </div>

        {/* Consultant vs Co-founder distinction */}
        <div className="bg-white border border-warm rounded-2xl overflow-hidden shadow-lg" data-testid="cf-distinction">
          <div className="p-6 md:p-8 border-b border-warm bg-cream">
            <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-accent-olive mb-2">{d.label}</p>
            <p className="font-serif italic text-xl md:text-2xl text-ink leading-snug">{d.line}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-warm">
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <X className="w-5 h-5 text-accent-terracotta" />
                <h4 className="font-serif text-lg md:text-xl text-ink">{d.consultant.title}</h4>
              </div>
              <ul className="space-y-2.5">
                {d.consultant.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink-muted">
                    <span className="text-accent-terracotta">·</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 md:p-8 bg-accent-olive text-parchment">
              <div className="flex items-center gap-2 mb-4">
                <Check className="w-5 h-5 text-parchment" />
                <h4 className="font-serif text-lg md:text-xl text-parchment">{d.coFounder.title}</h4>
              </div>
              <ul className="space-y-2.5">
                {d.coFounder.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-parchment/90">
                    <span className="text-accent-terracotta-light">·</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Your Journey With Us (5-Step Timeline)
// ─────────────────────────────────────────────────────────────────────────────
const JourneySection = () => {
  const { t } = useLanguage();
  const steps = t('journey.steps');
  const icons = [
    <Compass key="c" className="w-5 h-5" />,
    <Anchor key="a" className="w-5 h-5" />,
    <TrendingUp key="t" className="w-5 h-5" />,
    <ShieldCheck key="s" className="w-5 h-5" />,
    <Target key="g" className="w-5 h-5" />
  ];
  return (
    <section id="journey" className="py-20 md:py-28 lg:py-32 bg-cream" data-testid="journey-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-olive mb-4">{t('journey.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('journey.headlineLead')}<br />
            <span className="italic text-accent-olive">{t('journey.headlineEm')}</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-ink-light leading-relaxed">{t('journey.description')}</p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Horizontal connecting line (desktop) */}
          <div className="hidden lg:block absolute top-[3.25rem] left-0 right-0 h-px bg-accent-olive/30" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-4">
            {steps.map((s, i) => (
              <article key={i} className="relative bg-white border border-warm rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col" data-testid={`journey-step-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center justify-center w-11 h-11 rounded-full bg-accent-olive text-parchment shadow-md">
                    {icons[i]}
                  </div>
                  <span className="font-serif text-3xl text-accent-olive/30">{s.number}</span>
                </div>
                <p className="text-[10px] sm:text-xs tracking-widest uppercase text-accent-olive mb-2">{s.tag}</p>
                <h3 className="font-serif text-xl md:text-2xl text-ink mb-3 leading-snug">{s.title}</h3>
                <p className="text-sm text-ink-light leading-relaxed flex-1">{s.description}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Services (6 Pillars under one roof)
// ─────────────────────────────────────────────────────────────────────────────
const ServicesSection = () => {
  const { t } = useLanguage();
  const pillars = t('services.pillars');
  const icons = [
    <Building2 key="bf" className="w-5 h-5" />,
    <Calculator key="fs" className="w-5 h-5" />,
    <Users key="hr" className="w-5 h-5" />,
    <ScrollText key="adm" className="w-5 h-5" />,
    <Compass key="me" className="w-5 h-5" />,
    <Award key="gr" className="w-5 h-5" />
  ];
  return (
    <section id="services" className="relative py-20 md:py-28 lg:py-32 bg-parchment overflow-hidden" data-testid="services-section">
      <div className="absolute -top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-accent-olive/5 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-olive mb-4">{t('services.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('services.headlineLead')}<br />
            <span className="italic text-accent-olive">{t('services.headlineEm')}</span>
          </h2>
          <p
            className="font-sans text-base sm:text-lg text-ink-light leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t('services.description') }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {pillars.map((p, idx) => (
            <article
              key={idx}
              className="group relative bg-white border border-warm rounded-2xl p-6 sm:p-7 hover:shadow-xl hover:-translate-y-1 hover:border-accent-olive/30 transition-all duration-300 flex flex-col"
              data-testid={`service-pillar-${idx}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-accent-olive/10 text-accent-olive flex-shrink-0">
                  {icons[idx]}
                </div>
                <span className="text-[10px] tracking-widest uppercase text-accent-olive/70">{p.tag}</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl text-ink mb-1 leading-snug">{p.title}</h3>
              {p.subtitle && (
                <p className="text-xs italic text-accent-terracotta mb-2">{p.subtitle}</p>
              )}
              <p className="text-sm text-ink-light leading-relaxed mb-5">{p.description}</p>
              <ul className="space-y-2 mt-auto pt-4 border-t border-warm">
                {p.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink-light">
                    <Check className="w-3.5 h-3.5 mt-1 text-accent-terracotta flex-shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Right For You (Who + Comparison + Differentiators)
// ─────────────────────────────────────────────────────────────────────────────
const RightForYouSection = () => {
  const { t } = useLanguage();
  const who = t('rightForYou.whoItems');
  return (
    <section id="right-for-you" className="py-20 md:py-28 lg:py-32 bg-parchment" data-testid="right-for-you-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-olive mb-4">{t('rightForYou.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]">
            {t('rightForYou.headlineLead')}<br />
            <span className="italic text-accent-olive">{t('rightForYou.headlineEm')}</span>
          </h2>
        </div>

        {/* Who we work with */}
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-5 font-medium">{t('rightForYou.whoLabel')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {who.map((p, i) => (
              <div key={i} className="bg-white border border-warm rounded-xl p-5 sm:p-6 hover:border-accent-olive/40 hover:-translate-y-1 transition-all" data-testid={`who-we-work-with-${i}`}>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-olive/10 text-accent-olive mb-3">
                  <Users className="w-5 h-5" />
                </div>
                <h4 className="font-serif text-lg text-ink mb-2">{p.title}</h4>
                <p className="text-sm text-ink-muted leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Recognition (3 trusted partners)
// ─────────────────────────────────────────────────────────────────────────────
const RecognitionSection = () => {
  const { t } = useLanguage();
  const items = t('recognition.items');
  const accents = [
    { ring: "ring-accent-amber/40", bg: "bg-accent-amber/10 text-accent-amber", icon: <Shield className="w-5 h-5" /> },
    { ring: "ring-accent-indigo/40", bg: "bg-accent-indigo/10 text-accent-indigo", icon: <Building2 className="w-5 h-5" /> },
    { ring: "ring-accent-teal/40", bg: "bg-accent-teal/10 text-accent-teal", icon: <Handshake className="w-5 h-5" /> }
  ];
  return (
    <section id="recognition" className="py-20 md:py-28 lg:py-32 bg-gradient-to-b from-parchment to-cream/50" data-testid="recognition-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-teal mb-4">{t('recognition.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('recognition.headline')}
          </h2>
          <p className="font-sans text-base sm:text-lg text-ink-light leading-relaxed">{t('recognition.description')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 mb-10">
          {items.map((item, idx) => {
            const a = accents[idx];
            return (
              <a
                key={idx}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`group bg-white border border-warm rounded-2xl p-6 md:p-8 ring-1 ring-transparent hover:${a.ring} hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col`}
                data-testid={`recognition-card-${idx}`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${a.bg}`}>
                  {a.icon}
                </div>
                <span className="text-[10px] tracking-widest uppercase text-ink-muted mb-2">{item.badge}</span>
                <h3 className="font-serif text-xl md:text-2xl text-ink leading-snug mb-1.5">{item.name}</h3>
                <p className="font-sans text-sm text-ink-light italic mb-4">{item.role}</p>
                <p className="font-sans text-sm text-ink-muted leading-relaxed mb-5">{item.description}</p>
                <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-accent-teal group-hover:gap-2.5 transition-all">
                  {t('recognition.verify')}
                  <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </a>
            );
          })}
        </div>

        <div className="text-center">
          <p className="font-serif italic text-lg md:text-xl text-ink-light">
            <span className="text-accent-olive font-medium">1</span> {t('recognition.tagline')}
          </p>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Ventures (RETAINED — user requirement)
// ─────────────────────────────────────────────────────────────────────────────
const VenturesSection = () => {
  const { t } = useLanguage();
  const ventureMeta = [
    { url: "https://tourpilot.biz", bg: "bg-accent-amber", icon: <Plane className="w-5 h-5" /> },
    { url: "https://www.finndmc.com/", bg: "bg-accent-terracotta", icon: <MapPin className="w-5 h-5" /> },
    { url: "https://selkotili.com", bg: "bg-accent-teal", icon: <BookOpen className="w-5 h-5" /> },
    { url: "https://vanit.app/", bg: "bg-accent-rose", icon: <Rocket className="w-5 h-5" /> },
    { url: "https://bizvaluer.co", bg: "bg-accent-indigo", icon: <Calculator className="w-5 h-5" /> }
  ];
  const ventures = t('ventures.items').map((it, i) => ({ ...it, ...ventureMeta[i] }));
  return (
    <section id="ventures" className="py-14 md:py-20 lg:py-24 bg-cream" data-testid="ventures-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-10 md:mb-12">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-olive mb-3">{t('ventures.label')}</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl tracking-tight text-ink leading-tight">
            {t('ventures.headline')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {ventures.map((v) => {
            const inner = (
              <>
                <div className="flex flex-wrap items-center gap-2.5 mb-3">
                  <span className={`inline-flex items-center gap-1.5 ${v.bg} text-white px-2.5 py-1 rounded-full text-[11px] font-medium`}>
                    {v.icon}
                    {v.highlight}
                  </span>
                  {!v.comingSoon && (
                    <span className="text-ink-muted text-xs flex items-center gap-1 group-hover:text-ink transition-colors">
                      {v.url.replace('https://', '').replace(/\/$/, '')}
                      <ExternalLink className="w-3 h-3" />
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-ink mb-2 group-hover:text-accent-olive transition-colors">{v.name}</h3>
                <p className="font-sans text-sm text-ink-muted leading-relaxed">{v.description}</p>
              </>
            );
            const sharedClasses = "venture-card group block bg-white border border-warm rounded-2xl p-5 md:p-6 hover:shadow-md transition-all h-full";
            const testId = `venture-card-${v.name.toLowerCase().replace(/\./g, '-')}`;
            return v.comingSoon ? (
              <div key={v.name} className={`${sharedClasses} cursor-default`} data-testid={testId}>{inner}</div>
            ) : (
              <a key={v.name} href={v.url} target="_blank" rel="noopener noreferrer" className={sharedClasses} data-testid={testId}>{inner}</a>
            );
          })}
        </div>

        {/* NDA client ventures note */}
        <div className="mt-6 flex items-center gap-3 bg-white/60 border border-dashed border-warm rounded-xl px-4 py-3" data-testid="nda-ventures-note">
          <Shield className="w-4 h-4 text-accent-olive flex-shrink-0" />
          <p className="text-xs sm:text-sm text-ink-light leading-snug">
            <span className="font-medium text-ink">{t('ventures.ndaTitle')}</span> · {t('ventures.ndaBody')}
          </p>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Roadmap (3 phases)
// ─────────────────────────────────────────────────────────────────────────────
const RoadmapSection = () => {
  const { t } = useLanguage();
  const phases = t('roadmap.phases');
  return (
    <section id="roadmap" className="py-20 md:py-28 lg:py-32 bg-parchment" data-testid="roadmap-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-terracotta mb-4">{t('roadmap.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('roadmap.headlineLead')}<br />
            <span className="italic text-accent-terracotta">{t('roadmap.headlineEm')}</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-ink-light leading-relaxed">{t('roadmap.description')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {phases.map((p, i) => (
            <article key={i} className="relative bg-white border border-warm rounded-2xl p-6 md:p-8 hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col" data-testid={`roadmap-phase-${i}`}>
              <div className="absolute top-6 right-6 font-serif text-5xl text-accent-terracotta/15 leading-none">0{i + 1}</div>
              <span className="text-[10px] tracking-widest uppercase text-accent-terracotta mb-2">{p.tag}</span>
              <h3 className="font-serif text-2xl md:text-3xl text-ink mb-5 leading-snug">{p.title}</h3>
              <ul className="space-y-3 mt-auto">
                {p.items.map((it, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-ink-light leading-relaxed">
                    <ChevronRight className="w-3.5 h-3.5 mt-1 text-accent-terracotta flex-shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Are You the Right Fit? (5 detailed audience profiles)
// ─────────────────────────────────────────────────────────────────────────────
const AudienceSection = () => {
  const { t } = useLanguage();
  const items = t('audience.items');
  const icons = [
    <Lightbulb key="dt" className="w-5 h-5" />,
    <Building2 key="co" className="w-5 h-5" />,
    <Briefcase key="in" className="w-5 h-5" />,
    <Sparkles key="ie" className="w-5 h-5" />,
    <Shield key="ps" className="w-5 h-5" />
  ];
  return (
    <section id="audience" className="py-20 md:py-28 lg:py-32 bg-cream" data-testid="audience-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-olive mb-4">{t('audience.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('audience.headline')}
          </h2>
          <p className="font-sans text-base sm:text-lg text-ink-light leading-relaxed">{t('audience.description')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((it, i) => (
            <div
              key={i}
              className="bg-white border border-warm rounded-2xl p-6 md:p-7 hover:shadow-xl hover:-translate-y-1 hover:border-accent-olive/30 transition-all duration-300"
              data-testid={`audience-card-${i}`}
            >
              <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-accent-olive/10 text-accent-olive mb-4">
                {icons[i]}
              </div>
              <h3 className="font-serif text-xl md:text-2xl text-ink mb-3 leading-snug">{it.title}</h3>
              <p className="text-sm text-ink-light leading-relaxed">{it.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Final CTA + Founder Card + Contact Form
// ─────────────────────────────────────────────────────────────────────────────
const FinalCtaSection = () => {
  const { t } = useLanguage();
  const c = t('finalCta.channels');
  return (
    <section id="contact" className="py-20 md:py-28 lg:py-32 bg-gradient-to-b from-parchment to-cream/60" data-testid="final-cta-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-10 md:mb-12">
          <p className="text-xs tracking-[0.25em] uppercase text-accent-olive mb-4">{t('finalCta.label')}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-5">
            {t('finalCta.headlineLead')}<br />
            <span className="italic text-accent-olive">{t('finalCta.headlineEm')}</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-ink-light leading-relaxed">{t('finalCta.description')}</p>
        </div>

        {/* Direct-contact channels strip — 2 cards on a row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 md:mb-14" data-testid="contact-channels-strip">
          <a
            href="mailto:sales@2ds.fi"
            className="bg-white border border-warm rounded-2xl p-5 md:p-6 flex items-start gap-4 hover:border-accent-olive/40 hover:-translate-y-0.5 hover:shadow-md transition-all group"
            data-testid="contact-email-channel"
          >
            <div className="w-10 h-10 rounded-lg bg-accent-olive/10 text-accent-olive flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] tracking-widest uppercase text-ink-muted">{c.emailLabel}</p>
              <p className="font-sans text-base text-ink group-hover:text-accent-olive transition-colors truncate">sales@2ds.fi</p>
            </div>
          </a>
          <div
            className="bg-white border border-warm rounded-2xl p-5 md:p-6 flex items-start gap-4"
            data-testid="contact-location-channel"
          >
            <div className="w-10 h-10 rounded-lg bg-accent-olive/10 text-accent-olive flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] tracking-widest uppercase text-ink-muted">{c.locationLabel}</p>
              <p className="font-sans text-sm text-ink leading-snug">{c.location}</p>
            </div>
          </div>
        </div>

        {/* Form — full width, centered */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-warm rounded-2xl p-6 md:p-10 shadow-lg">
            <div className="mb-6">
              <p className="text-xs tracking-[0.2em] uppercase text-accent-olive mb-2">{t('finalCta.formLabel')}</p>
              <p className="text-xs text-ink-muted">{t('finalCta.formNote')}</p>
            </div>
            <ContactForm />
          </div>

          {/* Ventures footer link — subtle inline row */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm" data-testid="contact-ventures-strip">
            <span className="text-[10px] tracking-widest uppercase text-ink-muted">{t('contact.venturesLabel')}</span>
            <a href="https://tourpilot.biz" target="_blank" rel="noopener noreferrer" className="text-ink hover:text-accent-olive transition-colors inline-flex items-center gap-1" data-testid="footer-link-tourpilot">TourPilot.biz <ExternalLink className="w-3 h-3" /></a>
            <a href="https://www.finndmc.com/" target="_blank" rel="noopener noreferrer" className="text-ink hover:text-accent-olive transition-colors inline-flex items-center gap-1" data-testid="footer-link-finndmc">FinnDMC.com <ExternalLink className="w-3 h-3" /></a>
            <a href="https://selkotili.com" target="_blank" rel="noopener noreferrer" className="text-ink hover:text-accent-olive transition-colors inline-flex items-center gap-1" data-testid="footer-link-selkotili">Selkotili.com <ExternalLink className="w-3 h-3" /></a>
            <a href="https://vanit.app/" target="_blank" rel="noopener noreferrer" className="text-ink hover:text-accent-olive transition-colors inline-flex items-center gap-1" data-testid="footer-link-vanit">Vanit.app <ExternalLink className="w-3 h-3" /></a>
            <a href="https://bizvaluer.co" target="_blank" rel="noopener noreferrer" className="text-ink hover:text-accent-olive transition-colors inline-flex items-center gap-1" data-testid="footer-link-bizvaluer">BizValuer.co <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────
const Footer = () => {
  const { t } = useLanguage();
  const cookieConsent = useCookieConsent();
  return (
    <footer className="bg-ink text-parchment py-16 md:py-20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-white rounded-lg p-1.5 flex-shrink-0" data-testid="footer-logo-pill">
                <img
                  src={LOGO_URL}
                  alt="2DS Finland Oy"
                  className="h-9 w-auto object-contain block"
                  style={{ mixBlendMode: 'multiply' }}
                />
              </div>
              <span className="font-serif text-xl">2DS Finland Oy</span>
            </div>
            <p className="text-parchment/60 text-sm mb-4 max-w-md leading-relaxed">{t('footer.description')}</p>
            <p className="text-parchment/40 text-xs">{t('hero.businessId')}: 3246359-5 · Espoo, Finland</p>
          </div>

          <div>
            <h4 className="font-sans text-sm tracking-widest uppercase text-parchment/40 mb-4">{t('footer.venturesLabel')}</h4>
            <div className="space-y-3">
              <a href="https://tourpilot.biz" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm">TourPilot.biz <ExternalLink className="w-3 h-3" /></a>
              <a href="https://selkotili.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm">Selkotili.com <ExternalLink className="w-3 h-3" /></a>
              <a href="https://bizvaluer.co" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm">BizValuer.co <ExternalLink className="w-3 h-3" /></a>
              <a href="https://vanit.app/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm">Vanit.app <ExternalLink className="w-3 h-3" /></a>
            </div>
          </div>

          <div>
            <h4 className="font-sans text-sm tracking-widest uppercase text-parchment/40 mb-4">{t('footer.companyLabel')}</h4>
            <div className="space-y-3">
              <a href="mailto:sales@2ds.fi" className="flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm">
                <Mail className="w-4 h-4" /> sales@2ds.fi
              </a>
              <Link to="/privacy-policy" className="block text-parchment/70 hover:text-parchment transition-colors text-sm" data-testid="footer-privacy-policy">{t('footer.privacyPolicy')}</Link>
              <Link to="/cookie-policy" className="block text-parchment/70 hover:text-parchment transition-colors text-sm" data-testid="footer-cookie-policy">{t('footer.cookiePolicy')}</Link>
              <Link to="/operation-policy" className="flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm" data-testid="footer-operation-policy">
                <ScrollText className="w-3 h-3" /> {t('footer.operationPolicy')}
              </Link>
              <button onClick={() => cookieConsent?.showSettings()} className="flex items-center gap-2 text-parchment/70 hover:text-parchment transition-colors text-sm" data-testid="footer-cookie-settings">
                <Settings className="w-3 h-3" /> {t('footer.cookieSettings')}
              </button>
              <div className="pt-2">
                <LanguageSwitcher variant="footer" />
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-parchment/10 mb-8" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-parchment/40 text-sm">© {new Date().getFullYear()} 2DS Finland Oy. {t('footer.copyright')}</p>
          <p className="text-parchment/40 text-sm italic font-serif">{t('footer.tagline')}</p>
        </div>
      </div>
    </footer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Home Composition (PDF-aligned narrative order)
// ─────────────────────────────────────────────────────────────────────────────
const HomePage = () => (
  <>
    <Navigation />
    <main>
      <HeroSection />
      <BarriersSection />
      <JourneySection />
      <ServicesSection />
      <RightForYouSection />
      <RecognitionSection />
      <VenturesSection />
      <FinalCtaSection />
    </main>
    <WhatsAppFloat />
    <Footer />
  </>
);

function App() {
  return (
    <div className="bg-parchment min-h-screen">
      <BrowserRouter>
        <LanguageProvider>
          <CookieConsentProvider>
            <VisitorTracker />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/operation-policy" element={<OperationPolicy />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            </Routes>
          </CookieConsentProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
